import React, { useState } from 'react';
import { Bell, BellOff, Volume2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';

/** Tiny per-device sound preference UI. Drops into prep panel headers. */
export default function AlertPrefsButton({ prefs, onChange, onTest }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          className={`h-8 rounded-sm font-sans text-xs border ${
            prefs.muted ? 'bg-zinc-700/40 text-zinc-400 border-zinc-700/40' : 'bg-gold/10 text-gold border-gold/30'
          }`}
          data-testid="alert-prefs-button"
          title="Sound & vibration settings"
        >
          {prefs.muted ? <BellOff className="w-3.5 h-3.5 mr-1.5" /> : <Bell className="w-3.5 h-3.5 mr-1.5" />}
          {prefs.muted ? 'Muted' : 'Alerts on'}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="bg-bg-secondary border border-white/10 rounded-sm"
        align="end"
        sideOffset={6}
        collisionPadding={8}
        style={{ width: 'min(18rem, calc(100vw - 1rem))' }}
        data-testid="alert-prefs-popover"
      >
        <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-sans mb-3">Order alerts (this device)</p>
        <div className="space-y-3">
          <label className="flex items-center justify-between text-sm text-white font-sans">
            <span>Mute on this device</span>
            <input
              type="checkbox"
              checked={prefs.muted}
              onChange={(e) => onChange({ muted: e.target.checked })}
              className="w-5 h-5 accent-gold"
              data-testid="alert-prefs-muted"
            />
          </label>
          <div>
            <p className="text-xs text-zinc-400 font-sans mb-1.5">Sound</p>
            <select
              value={prefs.sound}
              onChange={(e) => onChange({ sound: e.target.value })}
              disabled={prefs.muted}
              className="w-full bg-bg-primary border border-white/10 rounded-sm text-sm text-white font-sans px-2 py-1.5 disabled:opacity-50"
              data-testid="alert-prefs-sound"
            >
              <option value="chime">Chime — pleasant bell</option>
              <option value="bell">Bell — softer hotel bell</option>
              <option value="ring">Ring — urgent triple-ring</option>
              <option value="klaxon">Klaxon — alarm</option>
            </select>
          </div>
          <div>
            <p className="text-xs text-zinc-400 font-sans mb-1.5 flex items-center gap-1.5">
              <Volume2 className="w-3 h-3" /> Volume: {Math.round((prefs.volume || 0.7) * 100)}%
            </p>
            <input
              type="range" min="0.1" max="1" step="0.05"
              value={prefs.volume || 0.7}
              onChange={(e) => onChange({ volume: parseFloat(e.target.value) })}
              disabled={prefs.muted}
              className="w-full accent-gold disabled:opacity-50"
              data-testid="alert-prefs-volume"
            />
          </div>
          <Button
            size="sm"
            onClick={onTest}
            disabled={prefs.muted}
            className="w-full bg-gold/15 text-gold border border-gold/30 rounded-sm font-sans text-xs hover:bg-gold/25 disabled:opacity-50"
            data-testid="alert-prefs-test"
          >
            Test sound
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
